import { DefaultTheme, LocalDataKeys } from '@libs/types';
import * as React from 'react';
import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { selectTheme, useAppSelector } from '@libs/store';
import { useLocalData } from '@libs/hooks';
import { getThemeFonts } from './themeRender';

export type TSystemColorScheme = DefaultTheme.light | DefaultTheme.dark;

/** User-selectable theme preference; `system` follows the OS `prefers-color-scheme`. */
export type TThemePreference = TSystemColorScheme | 'system';

const SystemColorSchemeContext = createContext<TSystemColorScheme>(DefaultTheme.light);

export const useSystemColorScheme = (): TSystemColorScheme => useContext(SystemColorSchemeContext);

const getInitialColorScheme = (): TSystemColorScheme =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches
        ? DefaultTheme.dark
        : DefaultTheme.light;

const CoreProvider = ({ children }: { children: ReactNode }) => {
    const theme = useAppSelector(selectTheme);
    const fontsCss = useMemo(() => getThemeFonts(theme), [theme]);
    const [systemScheme, setSystemScheme] = useState<TSystemColorScheme>(getInitialColorScheme);
    const themePreference = useLocalData<TThemePreference>(LocalDataKeys.theme);

    useEffect(() => {
        if (window?.matchMedia) {
            const mq = window.matchMedia('(prefers-color-scheme: dark)');
            const handler = (e: MediaQueryListEvent) => setSystemScheme(e.matches ? DefaultTheme.dark : DefaultTheme.light);
            mq.addEventListener('change', handler);
            return () => mq.removeEventListener('change', handler);
        }
    }, []);

    // Explicit `light`/`dark` wins; `system` (or no stored preference yet) follows the OS.
    const colorScheme: TSystemColorScheme = themePreference === DefaultTheme.light || themePreference === DefaultTheme.dark ? themePreference : systemScheme;

    return (
        <SystemColorSchemeContext.Provider value={colorScheme}>
            {theme?.themeHash && (
                <style href={`core-theme-${theme.themeHash}`} precedence="default">{`
                    body {
                        background: ${colorScheme === DefaultTheme.dark ? '#141414' : '#FFF'};
                        color: ${colorScheme === DefaultTheme.dark ? '#FFF' : '#141414'};
                    }
                    ${fontsCss}
                `}</style>
            )}
            {children}
        </SystemColorSchemeContext.Provider>
    );
};

export { CoreProvider };
